<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ task ? 'Edit Task' : 'New Task' }}</h2>
        <button class="btn btn-icon modal-close" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <form @submit.prevent="submit">
        <div class="form-group">
          <label>Title *</label>
          <input v-model="form.title" class="form-control" placeholder="Task title" required maxlength="200" />
        </div>

        <div class="form-group">
          <label>Description</label>
          <textarea v-model="form.description" class="form-control" rows="3" placeholder="Optional details…"></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Urgency</label>
            <select v-model="form.urgency" class="form-control">
              <option value="low">🔵 Low</option>
              <option value="normal">🟣 Normal</option>
              <option value="high">🟡 High</option>
              <option value="critical">🔴 Critical</option>
            </select>
          </div>
          <div class="form-group">
            <label>Status</label>
            <select v-model="form.status" class="form-control">
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Project</label>
            <select v-model="form.project_id" class="form-control">
              <option :value="null">— None —</option>
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Due date</label>
            <input v-model="form.due_at" type="datetime-local" class="form-control" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Visibility</label>
            <div class="radio-group">
              <label class="radio-opt">
                <input type="radio" v-model="form.visibility" value="shared" />
                <span>🌐 Shared (team sees this)</span>
              </label>
              <label class="radio-opt">
                <input type="radio" v-model="form.visibility" value="private" />
                <span>🔒 Private (only you + assignee)</span>
              </label>
            </div>
          </div>
          <div class="form-group" v-if="isManager">
            <label>Assign to</label>
            <select v-model="form.assigned_to" class="form-control">
              <option :value="null">— Unassigned —</option>
              <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
          <div class="form-group" v-if="isManager">
            <label>Link to client</label>
            <select v-model="form.client_id" class="form-control">
              <option :value="null">— None —</option>
              <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
        </div>

        <p v-if="error" class="form-error">{{ error }}</p>

        <div class="modal-actions">
          <button type="button" class="btn btn-ghost" @click="$emit('close')">Cancel</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">
            {{ saving ? 'Saving…' : (task ? 'Save changes' : 'Create task') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import api from '@/api'
import { useAuth } from '@/composables/useAuth'

function toLocalDatetime(str) {
  if (!str) return ''
  const d = new Date(str)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default {
  name: 'TaskFormModal',
  props: {
    task:     { type: Object, default: null },
    projects: { type: Array,  default: () => [] },
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const { isManager } = useAuth()
    const saving  = ref(false)
    const error   = ref('')
    const users   = ref([])
    const clients = ref([])

    const form = reactive({
      title:       props.task?.title       || '',
      description: props.task?.description || '',
      urgency:     props.task?.urgency     || 'normal',
      status:      props.task?.status      || 'open',
      visibility:  props.task?.visibility  || 'shared',
      project_id:  props.task?.project_id  ?? null,
      assigned_to: props.task?.assigned_to ?? null,
      client_id:   props.task?.client_id   ?? null,
      due_at:      toLocalDatetime(props.task?.due_at),
    })

    onMounted(async () => {
      if (isManager()) {
        try { users.value   = (await api.getUsers()).data   || [] } catch {}
        try { clients.value = (await api.getClients()).data || [] } catch {}
      }
    })

    async function submit() {
      error.value  = ''
      saving.value = true
      try {
        const payload = {
          title:       form.title,
          description: form.description || null,
          urgency:     form.urgency,
          status:      form.status,
          visibility:  form.visibility,
          project_id:  form.project_id  || null,
          assigned_to: form.assigned_to || null,
          client_id:   form.client_id   || null,
          due_at:      form.due_at      || null,
        }
        let res
        if (props.task) {
          res = await api.updateTask(props.task.id, payload)
        } else {
          res = await api.createTask(payload)
        }
        emit('saved', res.data)
        emit('close')
      } catch (e) {
        error.value = e.message || 'Save failed'
      } finally {
        saving.value = false
      }
    }

    return { form, users, clients, saving, error, isManager, submit }
  },
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); }
.modal-close:hover { color: var(--text); }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.radio-group { display: flex; flex-direction: column; gap: 8px; }
.radio-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--muted);
}
.radio-opt:has(input:checked) { color: var(--text); }
.radio-opt input { accent-color: var(--primary); }

.form-error {
  color: var(--danger);
  font-size: 13px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(239,68,68,.1);
  border-radius: 6px;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
</style>
